import { useCallback, useMemo, useState } from 'react'
import {
  RegisterPickerDialog,
  type RegisterSearchState,
} from '@/components/extensions/registers/RegisterPickerDialog'
import type { RegisterPick } from '@/components/extensions/registers/SnapshotSearchResults'
import type { RegisterCountry } from '@/services/api/registers'
import type { GraphView } from '@/types/graph-view'
import {
  type GraphAnchor,
  initialRegisterQuery,
  initialSearchKind,
  preferredRegisterCountry,
  rememberRegisterCountry,
} from './anchor-query'
import {
  SNAPSHOT_SEARCH_MIN_CHARS,
  useImportRegisterNeighbourhood,
  useSnapshotSearch,
} from './hooks/use-registers'

const PAGE_SIZE = 8

export interface RegisterPickerProps {
  caseId: string
  /** The case entity the ring menu was opened on. The caller renders this
   *  component only while one is set, keyed on its id, so the search state below
   *  is seeded per open. */
  anchor: GraphAnchor
  onClose: () => void
  /** The written subgraph, with real Neo4j element ids, ready for the object set. */
  onImported: (view: GraphView, subjectLabel: string) => void
}

/**
 * The register picker, wired to its data.
 *
 * Owns the search state and the import mutation; the dialog itself is
 * presentational. Mounted per anchor and keyed on it by the caller, so the
 * `useState` initialisers below seed each open from the entity it was opened on.
 */
export function RegisterPicker({ caseId, anchor, onClose, onImported }: RegisterPickerProps) {
  const [search, setSearch] = useState<RegisterSearchState>(() => ({
    country: preferredRegisterCountry(),
    kind: initialSearchKind(anchor),
    query: initialRegisterQuery(anchor),
    page: 1,
  }))
  const [picked, setPicked] = useState<RegisterPick | null>(null)
  const [depth, setDepth] = useState<1 | 2>(1)

  const query = useSnapshotSearch(search.country, search.query, search.kind, PAGE_SIZE, search.page)
  const importer = useImportRegisterNeighbourhood(caseId)

  const onSearchChange = useCallback((patch: Partial<RegisterSearchState>) => {
    if (patch.country) rememberRegisterCountry(patch.country as RegisterCountry)
    setSearch(current => ({ ...current, ...patch }))
  }, [])

  const result = useMemo(() => {
    const status = (query.error as { status?: number } | null)?.status
    return {
      data: query.data,
      loading: query.isFetching,
      // A missing snapshot is a deployment fact, not a failed request — it gets
      // its own line rather than a raw error message.
      unavailable: status === 503,
      error: query.isError && status !== 503 ? (query.error as Error).message : null,
    }
  }, [query.data, query.error, query.isError, query.isFetching])

  const onConfirm = useCallback(() => {
    if (!picked) return
    importer.mutate(
      {
        country: search.country,
        subjectId: picked.id,
        anchorNodeId: anchor.id,
        depth,
        maxNodes: depth === 1 ? 60 : 120,
      },
      {
        onSuccess: view => {
          onImported(view, picked.label)
          onClose()
        },
      }
    )
  }, [anchor, depth, importer, onClose, onImported, picked, search.country])

  return (
    <RegisterPickerDialog
      open
      onOpenChange={open => {
        if (!open) onClose()
      }}
      subject={{
        name: anchor.name?.trim() || anchor.id,
        typeLabel: anchor.typeLabel ?? '',
      }}
      search={search}
      onSearchChange={onSearchChange}
      result={result}
      selection={{ picked, onPick: setPicked, depth, onDepthChange: setDepth }}
      importState={{
        onConfirm,
        pending: importer.isPending,
        error: importer.isError ? (importer.error as Error).message : null,
      }}
      pageSize={PAGE_SIZE}
      minChars={SNAPSHOT_SEARCH_MIN_CHARS}
    />
  )
}
